import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import professionalsService from '../../services/professionalsService';
import photoService from '../../services/photoService';

import { normalizeSocialLink } from '../../utils/socialLinksNormalizer';
import { authService } from '../../services/authService';
import ProfilePhotoUpload from '../../components/ProfilePhotoUpload';
import ProfessionalAvatar from '../../components/ProfessionalAvatar';
import TemplateSelector from './TemplateSelector';
import RichTextEditor from './RichTextEditor';
import { ImageSourceSelector } from './ImageSourceSelector';

/**
 * ProfessionalPageForm - Formulário completo para criar/editar páginas de profissionais
 * Integrado com PageEditorPage via React Hook Form e props
 *
 * 14 Seções em Accordion:
 * 1. Profissional
 * 2. Foto
 * 3. Informações Profissionais
 * 4. Sobre
 * 5. Contato
 * 6. Redes Sociais
 * 7. Agendamento
 * 8. Experiência/Formação
 * 9. Serviços Oferecidos
 * 10. Depoimentos
 * 11. Estilos
 * 12. Template
 * 13. URL da Página
 * 14. Submit (Publicar)
 */
const ProfessionalPageForm = ({
  form,
  onSubmit,
  submitting = false,
  templates = [],
  isEdit = false,
  professionals = [],
}) => {
  const { control, watch, formState: { errors }, setValue, trigger } = form;

  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);
  const [photoUploadingProfessional, setPhotoUploadingProfessional] = useState(null);
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');

  // Watch form values
  const professionalId = watch('professionalId');
  const displayName = watch('displayName');
  const photoUrl = watch('photoUrl');
  const shortId = watch('shortId');

  // Auto-generate slug from displayName (only on creation)
  useEffect(() => {
    if (displayName && !isEdit) {
      const normalized = displayName
        .toLowerCase()
        .replace(/[áàâã]/g, 'a')
        .replace(/[éè]/g, 'e')
        .replace(/[íì]/g, 'i')
        .replace(/[óòôõ]/g, 'o')
        .replace(/[úù]/g, 'u')
        .replace(/[ç]/g, 'c')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .substring(0, 50);

      if (normalized) {
        setValue('slug', normalized);
      }
    }
  }, [displayName, isEdit, setValue]);

  // Fill professional data when selected
  const handleProfessionalChange = useCallback((profId) => {
    setValue('professionalId', profId);
    const selected = professionals.find(p => p.id === profId);
    if (selected) {
      const displayNameValue = selected.title
        ? `${selected.title} ${selected.name}`
        : selected.name;
      
      setValue('displayName', displayNameValue);
      setValue('title', selected.title || '');
      setValue('specialty', selected.specialty || '');
      setValue('registrationNumber', selected.registrationNumber || '');
      
      // Derivar URL da foto pelo CDN (com cache-busting via photoService)
      const userInfo = authService.getUserInfo();
      const userId = userInfo?.userId;
      if (userId && profId) {
        setValue('photoUrl', photoService.getProfilePhotoUrl(userId, profId));
      }
    }
  }, [professionals, setValue]);

  // Handle photo upload
  const handlePhotoUpload = useCallback(async (file) => {
    if (!photoUploadingProfessional?.id) return;

    setPhotoUploadLoading(true);
    setPhotoUploadError('');

    try {
      await photoService.uploadProfilePhoto(file, photoUploadingProfessional.id);

      // Salvar timestamp para cache-busting e gerar URL atualizada
      const timestamp = Date.now();
      photoService.savePhotoTimestamp(photoUploadingProfessional.id, timestamp);

      const userInfo = authService.getUserInfo();
      const userId = userInfo?.userId;
      if (userId) {
        setValue('photoUrl', photoService.getProfilePhotoUrl(userId, photoUploadingProfessional.id));
      }

      setPhotoUploadOpen(false);
      setPhotoUploadingProfessional(null);
    } catch (error) {
      setPhotoUploadError(error.message || 'Erro ao fazer upload da foto');
    } finally {
      setPhotoUploadLoading(false);
    }
  }, [setValue, photoUploadingProfessional]);

  // Social media fields
  const socialMediaFields = [
    { key: 'instagram', label: 'Instagram' },
    { key: 'linkedin', label: 'LinkedIn' },
    { key: 'twitter', label: 'Twitter/X' },
    { key: 'youtube', label: 'YouTube' },
    { key: 'facebook', label: 'Facebook' },
    { key: 'tiktok', label: 'TikTok' },
    { key: 'threads', label: 'Threads' },
  ];

  // Handle social link blur - normalize the URL
  const handleSocialLinkBlur = useCallback((platform, currentValue) => {
    if (currentValue && currentValue.trim()) {
      const normalized = normalizeSocialLink(currentValue, platform);
      if (normalized !== currentValue) {
        setValue(`socialLinks.${platform}.url`, normalized);
      }
    }
  }, [setValue]);

  // Helper to add/remove list items
  const addListItem = (fieldName) => {
    const current = watch(fieldName) || [];
    const emptyItem = fieldName === 'experience'
      ? { title: '', institution: '', yearStart: new Date().getFullYear(), yearEnd: '', description: '' }
      : fieldName === 'services'
        ? { name: '', description: '', duration: '', price: '' }
        : { name: '', text: '', rating: 5, date: new Date().toISOString().split('T')[0] };

    if (current.length < (fieldName === 'experience' ? 10 : fieldName === 'services' ? 15 : 10)) {
      setValue(fieldName, [...current, emptyItem]);
    }
  };

  const removeListItem = (fieldName, index) => {
    const current = watch(fieldName) || [];
    setValue(fieldName, current.filter((_, i) => i !== index));
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* SEÇÃO 1: PROFISSIONAL */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Profissional
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="professionalId"
            control={control}
            rules={{ required: 'Profissional é obrigatório' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.professionalId}>
                <InputLabel>Selecione o Profissional</InputLabel>
                <Select
                  {...field}
                  label="Selecione o Profissional"
                  onChange={(e) => handleProfessionalChange(e.target.value)}
                  disabled={isEdit}
                >
                  {professionals.map((prof) => (
                    <MenuItem key={prof.id} value={prof.id}>
                      {prof.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.professionalId && (
                  <Typography variant="caption" color="error">
                    {errors.professionalId.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 2: TEMPLATE */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Template
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Controller
            name="templateId"
            control={control}
            rules={{ required: 'Template é obrigatório' }}
            render={({ field }) => (
              <TemplateSelector
                templates={templates}
                value={field.value}
                onChange={field.onChange}
                error={errors.templateId?.message}
              />
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 3: FOTO */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Foto do Profissional
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {professionalId && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Prévia Atual
              </Typography>
              <ProfessionalAvatar
                professionalId={professionalId}
                photoTimestamp={photoService.getPhotoTimestamp(professionalId)}
                name={displayName}
                sx={{ width: 120, height: 120 }}
              />
            </Box>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              if (professionalId) {
                const selected = professionals.find(p => p.id === professionalId);
                setPhotoUploadingProfessional(selected);
                setPhotoUploadOpen(true);
              }
            }}
            disabled={!professionalId || photoUploadLoading}
          >
            {photoUrl ? 'Alterar Foto' : 'Fazer Upload da Foto'}
          </Button>
          {photoUploadError && (
            <Alert severity="error">{photoUploadError}</Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 3: INFORMAÇÕES PROFISSIONAIS */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Informações Profissionais
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="displayName"
                control={control}
                rules={{
                  required: 'Nome completo é obrigatório',
                  maxLength: { value: 100, message: 'Máximo 100 caracteres' },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome Completo"
                    placeholder="Dr. João Silva"
                    fullWidth
                    error={!!errors.displayName}
                    helperText={errors.displayName?.message}
                    inputProps={{ maxLength: 100 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Título"
                    placeholder="Dr., Profa., Prof., etc."
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="specialty"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Especialidade"
                    placeholder="Cardiologista, Psicólogo, etc."
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="registrationNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Registro Profissional"
                    placeholder="CRM 12345, CRP 00000, etc."
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 4: SOBRE */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Sobre o Profissional
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="aboutHtml"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                value={field.value || ''}
                onChange={field.onChange}
                label="Texto sobre o profissional"
                placeholder="Conte sobre sua experiência, formação, valores..."
                maxLength={5000}
              />
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 5: CONTATO */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Contato
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="contact.phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefone"
                    placeholder="(11) 99999-9999"
                    fullWidth
                    inputProps={{ maxLength: 20 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="contact.whatsapp"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="WhatsApp"
                    placeholder="(11) 99999-9999"
                    fullWidth
                    inputProps={{ maxLength: 20 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="contact.email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    placeholder="contato@email.com"
                    type="email"
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="contact.website"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Site Pessoal"
                    placeholder="https://seusite.com"
                    fullWidth
                    inputProps={{ maxLength: 200 }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 6: REDES SOCIAIS */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Redes Sociais
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Deixe em branco se não usar
          </Typography>
          <Grid container spacing={2}>
            {socialMediaFields.map((field) => (
              <Grid item xs={12} sm={6} key={field.key}>
                <Controller
                  name={`socialLinks.${field.key}.url`}
                  control={control}
                  defaultValue=""
                  render={({ field: inputField }) => (
                    <TextField
                      {...inputField}
                      label={field.label}
                      fullWidth
                      size="small"
                      onBlur={() => {
                        inputField.onBlur();
                        handleSocialLinkBlur(field.key, inputField.value);
                      }}
                      helperText="@usuario ou URL completa"
                    />
                  )}
                />
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 7: AGENDAMENTO */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Agendamento
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Selecione os locais onde este profissional oferece agendamento
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Funcionalidade de carregamento de locais e agendas será implementada via integração com API
          </Typography>
          <Controller
            name="booking.showBookingSection"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Mostrar seção de agendamento nesta página"
              />
            )}
          />
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 8: EXPERIÊNCIA/FORMAÇÃO */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Experiência / Formação
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Máximo 10 itens
            </Typography>
            {(watch('experience') || []).length < 10 && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => addListItem('experience')}
                size="small"
                variant="outlined"
              >
                Adicionar
              </Button>
            )}
          </Box>
          {(watch('experience') || []).map((item, index) => (
            <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Item {index + 1}</Typography>
                <IconButton
                  size="small"
                  onClick={() => removeListItem('experience', index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name={`experience.${index}.title`}
                    control={control}
                    defaultValue={item.title || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Título"
                        placeholder="Residência em Cardiologia"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`experience.${index}.institution`}
                    control={control}
                    defaultValue={item.institution || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Instituição"
                        placeholder="Hospital das Clínicas"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name={`experience.${index}.yearStart`}
                    control={control}
                    defaultValue={item.yearStart || new Date().getFullYear()}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Ano Início"
                        type="number"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Controller
                    name={`experience.${index}.yearEnd`}
                    control={control}
                    defaultValue={item.yearEnd || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Ano Fim"
                        placeholder="(deixe vazio para 'Atual')"
                        type="number"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={`experience.${index}.description`}
                    control={control}
                    defaultValue={item.description || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Descrição"
                        placeholder="Mais detalhes sobre esta experiência"
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 9: SERVIÇOS OFERECIDOS */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Serviços Oferecidos
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Máximo 15 itens
            </Typography>
            {(watch('services') || []).length < 15 && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => addListItem('services')}
                size="small"
                variant="outlined"
              >
                Adicionar
              </Button>
            )}
          </Box>
          {(watch('services') || []).map((item, index) => (
            <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Serviço {index + 1}</Typography>
                <IconButton
                  size="small"
                  onClick={() => removeListItem('services', index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name={`services.${index}.name`}
                    control={control}
                    defaultValue={item.name || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nome do Serviço"
                        placeholder="Consulta de Rotina"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={`services.${index}.description`}
                    control={control}
                    defaultValue={item.description || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Descrição"
                        placeholder="Avaliação clínica completa"
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`services.${index}.duration`}
                    control={control}
                    defaultValue={item.duration || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Duração Estimada"
                        placeholder="30 min"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`services.${index}.price`}
                    control={control}
                    defaultValue={item.price || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Preço"
                        placeholder="R$ 350,00"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 10: DEPOIMENTOS */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Depoimentos / Opiniões
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Máximo 10 itens
            </Typography>
            {(watch('testimonials') || []).length < 10 && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => addListItem('testimonials')}
                size="small"
                variant="outlined"
              >
                Adicionar
              </Button>
            )}
          </Box>
          {(watch('testimonials') || []).map((item, index) => (
            <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Depoimento {index + 1}</Typography>
                <IconButton
                  size="small"
                  onClick={() => removeListItem('testimonials', index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`testimonials.${index}.name`}
                    control={control}
                    defaultValue={item.name || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Nome"
                        placeholder="Maria Silva"
                        fullWidth
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`testimonials.${index}.date`}
                    control={control}
                    defaultValue={item.date || new Date().toISOString().split('T')[0]}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Data"
                        type="date"
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={`testimonials.${index}.text`}
                    control={control}
                    defaultValue={item.text || ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Depoimento"
                        placeholder="Conte sua experiência..."
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name={`testimonials.${index}.rating`}
                    control={control}
                    defaultValue={item.rating || 5}
                    render={({ field }) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">Avaliação:</Typography>
                        <Rating
                          {...field}
                          max={5}
                          onChange={(e, value) => field.onChange(value)}
                        />
                      </Box>
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 11: ESTILOS */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Estilos e Customização
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="customStyles.primaryColor"
                control={control}
                defaultValue="#0d6efd"
                render={({ field }) => (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                      Cor Primária
                    </Typography>
                    <TextField
                      {...field}
                      type="color"
                      fullWidth
                      sx={{ height: 50 }}
                    />
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="customStyles.backgroundColor"
                control={control}
                defaultValue="#ffffff"
                render={({ field }) => (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                      Cor de Fundo
                    </Typography>
                    <TextField
                      {...field}
                      type="color"
                      fullWidth
                      sx={{ height: 50 }}
                    />
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="customStyles.textColor"
                control={control}
                defaultValue="#212529"
                render={({ field }) => (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                      Cor de Texto
                    </Typography>
                    <TextField
                      {...field}
                      type="color"
                      fullWidth
                      sx={{ height: 50 }}
                    />
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="customStyles.fontFamily"
                control={control}
                defaultValue="Inter"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Família de Fonte</InputLabel>
                    <Select {...field} label="Família de Fonte">
                      <MenuItem value="Inter">Inter</MenuItem>
                      <MenuItem value="Roboto">Roboto</MenuItem>
                      <MenuItem value="Poppins">Poppins</MenuItem>
                      <MenuItem value="Lato">Lato</MenuItem>
                      <MenuItem value="Open Sans">Open Sans</MenuItem>
                      <MenuItem value="Montserrat">Montserrat</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="customStyles.fontSize"
                control={control}
                defaultValue="medium"
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Tamanho de Fonte</InputLabel>
                    <Select {...field} label="Tamanho de Fonte">
                      <MenuItem value="pequeno">Pequeno</MenuItem>
                      <MenuItem value="medium">Médio</MenuItem>
                      <MenuItem value="grande">Grande</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                Imagem de Fundo (Hero)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Imagem de fundo para a seção hero da página. Se não definida, usa a cor primária.
              </Typography>
              <Controller
                name="customStyles.backgroundImage"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <ImageSourceSelector
                    imageUrl={field.value}
                    onChange={(url) => field.onChange(url || '')}
                    onError={(err) => console.error('[ProfessionalPageForm] Background image error:', err)}
                    title="Imagem de Fundo"
                  />
                )}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* SEÇÃO 12: (movida para Seção 2, logo após Profissional) */}

      {/* SEÇÃO 13: URL DA PÁGINA */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            URL da Página
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            URL amigável para sua página (auto-gerada do nome, pode editar)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Slug (URL amigável)
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 2,
              p: 1.5,
              bgcolor: '#f9f9f9',
              borderRadius: 1,
              border: '1px solid #eee',
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
              {(import.meta.env.VITE_PAGES_URL || 'https://pages.agendario.com.br').replace('https://', '')}/
            </Typography>
            <Controller
              name="slug"
              control={control}
              rules={{
                required: 'Slug é obrigatório',
                pattern: {
                  value: /^[a-z0-9-]+$/,
                  message: 'Use apenas letras minúsculas, números e hífens',
                },
                maxLength: { value: 50, message: 'Máximo 50 caracteres' },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="seu-slug"
                  variant="standard"
                  size="small"
                  error={!!errors.slug}
                  inputProps={{
                    maxLength: 50,
                    style: { fontFamily: 'monospace', fontSize: '0.9rem' },
                  }}
                  sx={{
                    flex: 1,
                    '& .MuiInput-underline:before': { borderBottomColor: '#ddd' },
                    '& .MuiInput-underline:hover:before': { borderBottomColor: '#999' },
                  }}
                />
              )}
            />
            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
              {isEdit ? `-${shortId}` : '-(codigo dinamico)'}
            </Typography>
          </Box>
          {errors.slug && (
            <Typography variant="caption" color="error">
              {errors.slug.message}
            </Typography>
          )}
          {!isEdit && (
            <>
              <Typography variant="caption" color="text.secondary">
                Auto-preenchido do nome, mas você pode editar
              </Typography>
              <Typography variant="caption" color="text.secondary">
                O código dinâmico será gerado automaticamente após criar a página.
              </Typography>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Divider */}
      <Divider sx={{ my: 2 }} />

      {/* SEÇÃO 14: BOTÃO PUBLICAR */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" disabled={submitting} type="button">
          Cancelar
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={submitting}
          sx={{ minWidth: 150 }}
        >
          {submitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {isEdit ? 'Atualizar Página' : 'Publicar Página'}
        </Button>
      </Box>

      {/* Dialog para upload de foto */}
      <Dialog open={photoUploadOpen} onClose={() => setPhotoUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Alterar Foto do Profissional</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {photoUploadingProfessional && (
            <ProfilePhotoUpload
              onUpload={handlePhotoUpload}
              loading={photoUploadLoading}
              error={photoUploadError}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoUploadOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfessionalPageForm;
